import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, NoteStatus, NoteType, Participant, SourceData, Trial } from "@/api/dtos";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialsContext from "@/contexts/data/TrialsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, List, Paper, Theme, Tooltip, Typography, styled, useTheme } from "@mui/material";
import { immerable } from "immer";
import { FC, memo, useCallback, useContext, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import NoteDetails from "./NoteDetails";
import NoteView from "./NoteView";
import NotesAndQueriesListSort, { DefaultSortableFields, INotesAndQueriesListSortable, NotesAndQueriesListFilter } from "./NotesAndQueriesListSort";


const Subtitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
}));

const FilterSortBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(-7),
    alignItems: 'stretch'
}));

const SortBox = styled(Paper)(({ theme }) => ({
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex'
}));

const SearchBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    minHeight: theme.spacing(6)
}));

const SearchButton = styled(Button)(({ theme }) => ({
    minWidth: theme.spacing(6),
    width: theme.spacing(6),
    alignSelf: 'stretch',
    padding: theme.spacing(0)
}));

const CountBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    minHeight: theme.spacing(6),
    marginRight: theme.spacing(2)
}));


interface IVirtuosoContext {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    theme: Theme;
    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;

    hideDetails?: boolean;
}

interface IVirtuosoInnerItem {
    index: number;
    item: Note;
    context: IVirtuosoContext;
}


// Item contents are cached properly with React.memo
const InnerItem =({ 
    index,
    item,
    context
}: IVirtuosoInnerItem) => {
    return (
        <NoteView
            {...context}
            data={item}
        />
    )
};

const MemoizedInnerItem = memo(InnerItem) as typeof InnerItem;

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: Note, context: IVirtuosoContext) => {
    return <MemoizedInnerItem index={index} item={item} context={context} />
}

interface INotesAndQueriesListProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    
    notesAndQueries?: Note[];

    canAdd?: boolean;
    hideDetails?: boolean;
}

interface INotesAndQueriesSearch extends Partial<Omit<Note, 'details' | typeof immerable>> {

}

interface IDetailsProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;
}

const Details: FC<IDetailsProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    trials,
    masterGroups,
    coordinatingGroups,
    institutions,
    participants,
    metadata,
    trialConfiguration,
    configuration,
    sourceData
}) => {
    return (
        <>
            <Subtitle variant="h2">
                Details
            </Subtitle>
            <Paper>
                <NoteDetails 
                    trialId={trialId}
                    masterGroupId={masterGroupId}
                    coordinatingGroupId={coordinatingGroupId}
                    institutionId={institutionId}
                    participantId={participantId}
                    studyEventOid={studyEventOid}
                    studyEventRepeat={studyEventRepeat}
                    caseReportFormOid={caseReportFormOid}
                    caseReportFormVersionOid={caseReportFormVersionOid}
                    itemGroupName={itemGroupName}
                    itemGroupRepeat={itemGroupRepeat}
                    itemName={itemName}

                    trials={trials}
                    masterGroups={masterGroups}
                    coordinatingGroups={coordinatingGroups}
                    institutions={institutions}
                    participants={participants}
                    metadata={metadata}
                    trialConfiguration={trialConfiguration}
                    configuration={configuration}
                    sourceData={sourceData}
                />
            </Paper>
        </>
    )
}

const NotesAndQueriesList: FC<INotesAndQueriesListProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    notesAndQueries,

    canAdd,
    hideDetails
}) => {
    const theme = useTheme();
    const { idMap: trials } = useContext(TrialsContext);
    const { idMap: masterGroups } = useContext(MasterGroupsContext);
    const { idMap: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { idMap: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const [urlSearchParams, setUrlSearchParams] = useSearchParams();

    const searchData = useMemo(() => {
        const search: INotesAndQueriesSearch = {
            
        };

        if (!trialId && urlSearchParams.has('trialId')) {
            const query = urlSearchParams.get('trialId');

            search.trialId = query ? query as unknown as number : undefined;
        }

        if (!masterGroupId && urlSearchParams.has('masterGroupId')) {
            const query = urlSearchParams.get('masterGroupId');

            search.masterGroupId = query ? query as unknown as number : undefined;
        }

        if (!coordinatingGroupId && urlSearchParams.has('coordinatingGroupId')) {
            const query = urlSearchParams.get('coordinatingGroupId');

            search.coordinatingGroupId = query ? query as unknown as number : undefined;
        }

        if (!institutionId && urlSearchParams.has('institutionId')) {
            const query = urlSearchParams.get('institutionId');

            search.institutionId = query ? query as unknown as number : undefined;
        }

        if (!participantId && urlSearchParams.has('participantId')) {
            const query = urlSearchParams.get('participantId');

            search.participantId = query ? query as unknown as number : undefined;
        }

        if (!studyEventOid && urlSearchParams.has('studyEventOid')) {
            const query = urlSearchParams.get('studyEventOid');

            search.studyEventOid = query ? query : undefined;
        }

        if (!studyEventRepeat && urlSearchParams.has('studyEventRepeat')) {
            const query = urlSearchParams.get('studyEventRepeat');

            search.studyEventRepeat = query ? +query : undefined;
        }

        if (!caseReportFormOid && urlSearchParams.has('caseReportFormOid')) {
            const query = urlSearchParams.get('caseReportFormOid');

            search.caseReportFormOid = query ? query : undefined;
        }

        if (!caseReportFormVersionOid && urlSearchParams.has('caseReportFormVersionOid')) {
            const query = urlSearchParams.get('caseReportFormVersionOid');

            search.caseReportFormVersionOid = query ? query : undefined;
        }

        if (!itemGroupName && urlSearchParams.has('itemGroupName')) {
            const query = urlSearchParams.get('itemGroupName');

            search.masterGroupId = query ? +query : undefined;
        }

        if (!itemGroupRepeat && urlSearchParams.has('itemGroupRepeat')) {
            const query = urlSearchParams.get('itemGroupRepeat');

            search.itemGroupRepeat = query ? +query : undefined;
        }

        if (!itemName && urlSearchParams.has('itemName')) {
            const query = urlSearchParams.get('itemName');

            search.itemName = query ? query : undefined;
        }

        

        if (urlSearchParams.has('type')) {
            const query = urlSearchParams.get('type');

            search.type = query ? +query : undefined;
        }

        
        if (urlSearchParams.has('status')) {
            const query = urlSearchParams.get('status');

            search.status = query ? +query : undefined;
        }

        return search;
    }, 
    [
        urlSearchParams,
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    ])

    const virtuosoContext = useMemo(() => {
        return {
            theme,
            trialId: trialId ?? searchData.trialId,
            masterGroupsId: masterGroupId ?? searchData.masterGroupId,
            coordinatingGroupId: coordinatingGroupId ?? searchData.coordinatingGroupId,
            institutionId: institutionId ?? searchData.institutionId,
            participantId: participantId ?? searchData.participantId,
            studyEventOid: studyEventOid ?? searchData.studyEventOid,
            studyEventRepeat: studyEventRepeat ?? searchData.studyEventRepeat,
            caseReportFormOid: caseReportFormOid ?? searchData.caseReportFormOid,
            caseReportFormVersionOid: caseReportFormVersionOid ?? searchData.caseReportFormVersionOid,
            itemGroupName: itemGroupName ?? searchData.itemGroupName,
            itemGroupRepeat: itemGroupRepeat ?? searchData.itemGroupRepeat,
            itemName: itemName ?? searchData.itemName,

            trials: trials,
            masterGroups,
            coordinatingGroups,
            institutions,
            participants,
            metadata,
            trialConfiguration,
            configuration,
            sourceData,
        }
    }, [
        theme,
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        searchData
    ]);

    // const searchableFields = useMemo(() => {
    //     const context = {
    //         trials,
    //         masterGroups,
    //         coordinatingGroups,
    //         institutions,
    //         participants
    //     }

    //     return [
    //         !trialId && trials ? DefaultSearchableFields.trial(context) : undefined,
    //         !masterGroupId && masterGroups ? DefaultSearchableFields.masterGroup(context) : undefined,
    //         !coordinatingGroupId && coordinatingGroups ? DefaultSearchableFields.coordinatingGroup(context) : undefined,
    //         !institutionId && institutions ? DefaultSearchableFields.institution(context) : undefined,
    //         !participantId && participants ? DefaultSearchableFields.participant(context) : undefined,

    //         DefaultSearchableFields.type(context),
    //         DefaultSearchableFields.status(context)
    //     ].filter(x => !!x) as ISearchField<any>[]
    // }, [
    //     trials,
    //     masterGroups,
    //     coordinatingGroups,
    //     institutions,
    //     participants,

    //     trialId,
    //     masterGroupId,
    //     coordinatingGroupId,
    //     institutionId,
    //     participantId
    // ]);

    const sortableFields = useMemo(() => {
        return [
            // !trialId && trials && !searchData.trialId ? DefaultSortableFields.trial : undefined,
            // !masterGroupId && masterGroups && !searchData.masterGroupId ? DefaultSortableFields.masterGroup : undefined,
            // !coordinatingGroupId && coordinatingGroups && !searchData.coordinatingGroupId ? DefaultSortableFields.coordinatingGroup : undefined,
            // !institutionId && institutions && !searchData.institutionId ? DefaultSortableFields.institution : undefined,
            // !participantId && participants && !searchData.participantId ? DefaultSortableFields.participant : undefined,

            DefaultSortableFields.type,
            DefaultSortableFields.status,
            DefaultSortableFields.dateCreated
        ].filter(x => !!x) as INotesAndQueriesListSortable[]
    }, [
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        searchData
    ]);

    // const [searchOpen, setSearchOpen] = useState(false);
    const [selectedSort, setSelectedSort] = useState<undefined | {field: INotesAndQueriesListSortable; direction: 'asc' | 'desc'}>(undefined);

    const onSortChange = useCallback((sort : {field: INotesAndQueriesListSortable; direction: 'asc' | 'desc'}) => {
        setSelectedSort(sort)
    }, [setSelectedSort])
    
    // const onSearchClose = useCallback(() => {
    //     setSearchOpen(false);
    // }, [setSearchOpen])

    // const onSearchOk = useCallback(async (data?: ISearchForm) => {
    //     const newSearch = {...data};

    //     if (newSearch.participantId) {
    //         const participant = participants?.[newSearch.participantId];

    //         newSearch.trialId = participant?.trialId;
    //         newSearch.masterGroupId = participant?.masterGroupId;
    //         newSearch.coordinatingGroupId = participant?.coordinatingGroupId;
    //         newSearch.institutionId = participant?.institutionId;
    //     }
    //     else if (newSearch.institutionId) {
    //         const institution = institutions?.[newSearch.institutionId];

    //         newSearch.trialId = institution?.trialId;
    //         newSearch.masterGroupId = institution?.masterGroupId;
    //         newSearch.coordinatingGroupId = institution?.coordinatingGroupId;
    //     }
    //     else if (newSearch.coordinatingGroupId) {
    //         const coordinatingGroup = coordinatingGroups?.[newSearch.coordinatingGroupId];

    //         newSearch.trialId = coordinatingGroup?.trialId;
    //         newSearch.masterGroupId = coordinatingGroup?.masterGroupId;
    //     }
    //     else if (newSearch.masterGroupId) {
    //         const masterGroup = masterGroups?.[newSearch.masterGroupId];

    //         newSearch.trialId = masterGroup?.trialId;
    //     }

    //     for (let key in newSearch) {
    //         if (newSearch[key] === undefined) {
    //             delete newSearch[key];
    //         }
    //     }

    //     setUrlSearchParams(newSearch as any)
    // }, [
    //     trials,
    //     masterGroups,
    //     coordinatingGroups,
    //     institutions,
    //     participants,

    //     trialId,
    //     masterGroupId,
    //     coordinatingGroupId,
    //     institutionId,
    //     participantId,
        
    //     setUrlSearchParams
    // ])


    // const filterCount = useMemo(() => {
    //     const searchedFields = searchableFields?.filter(x => !!searchData?.[x.key as keyof INotesAndQueriesSearch]) ?? [];

    //     return searchedFields.length;
    // }, [searchData, searchableFields]);

    const sortedListItems = useMemo(() => {

        let newData = notesAndQueries?.filter(x => {
            for (let key in searchData) {
                if (
                    !!searchData[key as keyof INotesAndQueriesSearch] && 
                    searchData[key as keyof INotesAndQueriesSearch] != x[key as keyof Note]
                ) {
                    return false;
                }
            }

            return true;
        }) ?? [];

        if (!!selectedSort?.field  && !!selectedSort.direction && newData) {
            const context = {
                trials,
                masterGroups,
                coordinatingGroups,
                institutions,
                participants
            }

            newData = newData.sort((a,b) => selectedSort.field.sort(a,b,context) * (selectedSort.direction === 'desc' ? -1 : 1));
        }

        return newData;
    }, 
    [
        notesAndQueries, 
        searchData, 
        selectedSort,
        
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants
    ]);

    const counts = useMemo(() => {
        const types = {
            [NoteType.Annotation]: 0,
            [NoteType.InternalQuery]: 0,
            [NoteType.ProtocolDeviation]: 0,
            [NoteType.ImportantProtocolDeviation]: 0,
            [NoteType.ReasonForChange]: 0,
            [NoteType.SiteQuery]: 0,
            [NoteType.System]: 0
        }

        const statuses = {
            [NoteStatus.New]: 0,
            [NoteStatus.Closed]: 0,
            [NoteStatus.NotApplicable]: 0,
            [NoteStatus.ResolutionProposed]: 0,
            [NoteStatus.Updated]: 0
        }

        let total = 0;

        console.log(notesAndQueries);
        
        if (notesAndQueries) {
            for (let note of notesAndQueries) {
                types[note.type!]++;
                statuses[note.status!]++;
                total++;
            }
        }

        return {
            types,
            statuses,
            total
        }
    }, [notesAndQueries])

    const location = useLocation();

    const hasSearch = true;
    const hasSort = true;

    return (
        <>
            {
                !hideDetails && (
                    <Details
                        trialId={trialId ?? searchData.trialId}
                        masterGroupId={masterGroupId ?? searchData.masterGroupId}
                        coordinatingGroupId={coordinatingGroupId ?? searchData.coordinatingGroupId}
                        institutionId={institutionId ?? searchData.institutionId}
                        participantId={participantId ?? searchData.participantId}
                        studyEventOid={studyEventOid ?? searchData.studyEventOid}
                        studyEventRepeat={studyEventRepeat ?? searchData.studyEventRepeat}
                        caseReportFormOid={caseReportFormOid ?? searchData.caseReportFormOid}
                        caseReportFormVersionOid={caseReportFormVersionOid ?? searchData.caseReportFormVersionOid}
                        itemGroupName={itemGroupName ?? searchData.itemGroupName}
                        itemGroupRepeat={itemGroupRepeat ?? searchData.itemGroupRepeat}
                        itemName={itemName ?? searchData.itemName}
        
                        trials={trials}
                        masterGroups={masterGroups}
                        coordinatingGroups={coordinatingGroups}
                        institutions={institutions}
                        participants={participants}
                        metadata={metadata}
                        trialConfiguration={trialConfiguration}
                        configuration={configuration}
                        sourceData={sourceData}
                    />
                )
            }      
            <Subtitle variant="h2">
                Notes
            </Subtitle>
            <FilterSortBox>
                <CountBox>
                    {sortedListItems.length} Items
                </CountBox>
                <SortBox>
                    <NotesAndQueriesListFilter
                        onChange={(type, status) => {
                            let data: any = {};

                            if (type) {
                                data.type = type;
                            }
                            
                            if (status) {
                                data.status = status;
                            }

                            setUrlSearchParams(data)
                        }}
                        statuses={counts.statuses}
                        types={counts.types}
                        selectedStatus={searchData.status}
                        selectedType={searchData.type}
                    />
                </SortBox>
                {/* {
                    hasSearch && (
                        <SearchBox>
                            <Tooltip title="Filter">
                                <SearchButton onClick={() => setSearchOpen(true)} color={!!filterCount ? "primary" : "inherit"} variant="contained">
                                    <FontAwesomeIcon icon={AppSettings.icons.filter} size="lg" />
                                </SearchButton>
                            </Tooltip>
                        </SearchBox>
                    )
                } */}
                {
                    hasSort && (
                        <SortBox>
                            <NotesAndQueriesListSort 
                                sortableFields={sortableFields}
                                sortKey="notes-and-queries"
                                onChange={onSortChange}
                            />
                        </SortBox>
                    )
                }
                {
                    canAdd && AppSettings.allowNotesAndQueriesEdit && (
                        <SearchBox sx={{ml:2}}>
                            <Tooltip title="Add">
                                <SearchButton 
                                    variant="contained" 
                                    color="primary"
                                    //@ts-ignore
                                    component={Link}
                                    to={`${location.pathname}/add`}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" />
                                </SearchButton>
                            </Tooltip>
                        </SearchBox>
                    )
                }
            </FilterSortBox>

            {
                sortedListItems.length === 0 && (
                    <Alert
                        severity="warning"
                    >
                        <AlertTitle>No Notes or Queries</AlertTitle>
                        No notes or queries were found that match the requested criteria.
                    </Alert> 
                )
            }
            <List disablePadding>
                {
                    sortedListItems.length > 20 && (
                        <Virtuoso 
                            useWindowScroll
                            data={sortedListItems}
                            context={virtuosoContext}
                            itemContent={virtuosoItemContent}
                        />  
                    )
                }
                {
                    sortedListItems.length <= 20 && (
                        sortedListItems.map((x, i) => (
                            <MemoizedInnerItem key={i} index={i} item={x} context={virtuosoContext} />
                        ))
                    )
                }              
            </List>
            {/* <SearchDialog 
                open={searchOpen}
                searchFields={searchableFields}
                formData={searchData}
                onSave={onSearchOk}
                onClose={onSearchClose}
            /> */}
        </>
    )
}

export default NotesAndQueriesList;